import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";
import { LEGEND_POSITIONS, LineGraph } from "@similarweb/pro-logic-components";
import type { GenericGraphPropsSingleItem } from "@similarweb/pro-logic-components/dist/graphs/types";

import { type ICurrency, currenciesList } from "app/common/constants/currencies";
import { ChartContainerNoData } from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import { decimalNumberFilter } from "app/filters/ngFilters";
import { EGranularities } from "app/pages/app-intelligence/constants";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { SwitchersContainer } from "app/pages/website-analysis/traffic-sources/search/overview/SearchOverviewGraph/Components/StyledComponents";
import { CurrencySelector } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/CurrencySelector";
import DurationService from "app/services/DurationService";
import { mapSearchAdsSpend } from "app/services/searchAdsSpend/searchAdsSpendMapper";
import { isUnsupportedRange } from "app/services/searchAdsSpend/searchAdsSpendParams";
import { allTrackers } from "app/services/track/track";
import { NoData } from "pro-features/components/NoData/src/NoData";
import { CenteredFlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { isAllZeroSeries } from "../../Helpers/helpers";
import { getPpcChartConfig } from "../ppc-spend-chart/config/config";
import { SpendRatesNote } from "../ppc-spend-chart/SpendRatesNote";
import { UnsupportedRangeNotice } from "../ppc-spend-chart/UnsupportedRangeNotice";

import { transformCostPerVisitData } from "./transformCostPerVisitData";
import type { IUseCostPerVisitDataParams, SeriesLineItem } from "./types";
import { useCostPerVisitData } from "./useCostPerVisitData";

const CostPerVisitChartWrapper = styled.div`
    margin-bottom: 24px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const LoaderContainer = styled(CenteredFlexColumn)`
    padding: 26px 0 106px 0;
    height: 144px;
`;

type DataItem = GenericGraphPropsSingleItem<SeriesLineItem>;

interface ICostPerVisitChartProps {
    isShouldFetch?: boolean;
}

const CostPerVisitChart: FC<ICostPerVisitChartProps> = ({ isShouldFetch = true }) => {
    const { params } = useCurrentStateAndParams();
    const apiParams = proNavigator.getApiParams(params);

    const [selectedCurrency, setSelectedCurrency] = useState<ICurrency>(() => {
        const { performanceOverTimeChartCurrency } = proNavigator.getParams();
        return performanceOverTimeChartCurrency
            ? currenciesList.find((currency) => currency.name === performanceOverTimeChartCurrency)
            : currenciesList[0];
    });

    const isCompare = apiParams.key.split(",").length > 1;
    const { from, to, isWindow } = DurationService.getDurationData(params.duration, params.comparedDuration).forAPI;

    const hookParams: IUseCostPerVisitDataParams = {
        country: params.country,
        from,
        includeSubDomains: true,
        isWindow,
        keys: apiParams.key,
        selectedLegends: apiParams.key,
        timeGranularity: EGranularities.MONTHLY,
        to,
        webSource: params.webSource,
    };

    const { searchTrafficData, spendData, isLoading, isError, spendError } = useCostPerVisitData(
        hookParams,
        isShouldFetch,
    );

    const spend = useMemo(
        () => mapSearchAdsSpend(spendData, { currency: selectedCurrency.name, webSource: params.webSource }),
        [spendData, selectedCurrency.name, params.webSource],
    );

    const [lineGraphData, setLineGraphData] = useState<SimplifiedSeries<SeriesLineItem>>([]);

    useEffect(() => {
        if (searchTrafficData?.Data && spendData) {
            setLineGraphData(transformCostPerVisitData(spend, searchTrafficData.Data, isCompare, params.webSource));
        } else {
            setLineGraphData([]);
        }
    }, [searchTrafficData, spendData, spend, isCompare, params.webSource]);

    const chartConfig = useMemo(() => {
        return getPpcChartConfig({
            chartData: lineGraphData,
            selectedCurrency,
            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
            yAxisFormatter: ({ value }) => decimalNumberFilter()(value, 2),
            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
            volumeFilter: (value) => decimalNumberFilter()(value, 2),
        });
    }, [lineGraphData, selectedCurrency]);

    const handleLegendSelectionChange = (legendItems: Array<DataItem>) => {
        setLineGraphData((prevData) =>
            prevData.map((series) => {
                const updatedItem = legendItems.find((item) =>
                    series.id ? item.id === series.id : item.name === series.name,
                );
                return {
                    ...series,
                    visible: updatedItem?.visible ?? series.visible,
                };
            }),
        );
    };

    const trackLegendItemClicked = (legendItem: string, action: string) => {
        allTrackers.dispatchTrackEvent(
            "Click Filter",
            `${action === "add" ? "check" : "uncheck"}`,
            `Over Time Graph_Cost Per Visit/${legendItem}`,
        );
    };

    const onCurrencyChange = (currency) => {
        setSelectedCurrency(currency);

        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `Over Time Graph_Cost Per Visit/Currency/${currency.name}`,
        );

        const params = {
            performanceOverTimeChartCurrency: currency.name,
        };
        queueMicrotask(() => {
            proNavigator.applyUpdateParams(params, { reload: false });
        });
    };

    if (isLoading) {
        return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );
    }

    if (isUnsupportedRange(spendError)) {
        return <UnsupportedRangeNotice />;
    }

    if (isError || lineGraphData.length === 0 || isAllZeroSeries(lineGraphData)) {
        return (
            <ChartContainerNoData>
                <NoData />
            </ChartContainerNoData>
        );
    }

    return (
        <CostPerVisitChartWrapper data-automation="CostPerVisitChart">
            <ButtonsContainer data-automation="cost-per-visit-graph-buttons">
                <SwitchersContainer data-automation="cost-per-visit-graph-switchers">
                    {currenciesList ? (
                        <CurrencySelector
                            currencies={currenciesList}
                            onCurrencyChange={onCurrencyChange}
                            selectedCurrency={selectedCurrency}
                            withoutMargin={true}
                        />
                    ) : null}
                </SwitchersContainer>
            </ButtonsContainer>
            <LineGraph<DataItem>
                data={lineGraphData}
                shouldPresentLegend={isCompare}
                shouldPresentValueAtLegend
                withoutTooltipsPadding={false}
                legendPosition={LEGEND_POSITIONS.RIGHT}
                componentNameForTracking=""
                legendToggleHandlerCallback={handleLegendSelectionChange}
                // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                legendValueFilter={(value) => decimalNumberFilter()(value, 2)}
                graphConfig={chartConfig}
                legendsHeight={"100%"}
                legendsMaxWidth={250}
                legendGraphGap={24}
                customLegendTrackingHandler={trackLegendItemClicked}
            />
            <SpendRatesNote
                monthsWithoutRates={spend.monthsWithoutRates}
                currencyName={selectedCurrency.name}
            />
        </CostPerVisitChartWrapper>
    );
};

export default CostPerVisitChart;
